/// <amd-module name="Release/EnvironmentNode/EnvironmentChain" />
import * as React from "react";

import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { localeFormat } from "VSS/Core/Util/String";
import { Icon } from "VSSUI/Icon";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";

import { css } from "VSSUI/Util";

import { ReleaseEnvironment, ApprovalStatus, ReleaseApproval, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { IActiveReleaseEnvironmentNodeProps } from "./Types";
import { EnvironmentNode } from "EnvironmentNode";
import { getLatestDeploymentAttempt } from "./StatusComputer";

export interface IActiveReleaseEnvironmentsCellProps extends IVssComponentProperties {
    releaseId: number;
    definitionId: number;
    environments: ReleaseEnvironment[];
    definitionEnvironmentCurrentReleaseMap: { [key: number]: number };
    envNodeWidth?: number;
    envNodeMinWidth?: number;
    envNodeRightMargin?: number;
    visibleEnvCount?: number;
    onExpandClick: (widthOnExpand: number) => void;
    onReleaseFound?: () => void;
    releaseEnvironmentNodeCss?: string;
    preventClickOnNode?: boolean;
    projectId?: string;
    onNodeWithApprovalClicked?: (environmentId: number) => void;
    onNodeWithMIClicked?: (environmentId: number) => void;
}

export class EnvironmentsChain extends VssComponent<IActiveReleaseEnvironmentsCellProps, {}> {

    public constructor(props: IActiveReleaseEnvironmentsCellProps) {
        super(props);
    }

    public render(): React.ReactNode {
        const hiddenEnvCount = this.props.visibleEnvCount ? this.props.environments.length - this.props.visibleEnvCount : 0;
        let content = null;
        if (hiddenEnvCount > 0) {
            content = this._getEnvironmentsChainWithShowMoreButton();
        }
        else {
            content = this._getAllEnvironmentsChain();
        }

        return (<ErrorBoundary handlerComponentType="tfs.page-error-handler">
            {content}
        </ErrorBoundary>);
    }

    private _getAllEnvironmentsChain(): JSX.Element {
        const nodes = this._getNodes(this.props.environments);
        return (
            <div className={this.props.cssClass}> {nodes} </div>
        );
    }

    private _getEnvironmentsChainWithShowMoreButton(): JSX.Element {
        const visibleEnvironments = this.props.environments.slice(0, this.props.visibleEnvCount);
        const visibleNodes = this._getNodes(visibleEnvironments);
        return (
            <div className={css(this.props.cssClass, "active-rel-env-cell-with-more-button")}>
                {visibleNodes}
                {this._getShowMoreEnvironmentsButton()}
            </div>
        );
    }

    private _getNodes(environments: ReleaseEnvironment[]): JSX.Element[] {
        let nodes = environments.map((env) => {
            let nodeProps: IActiveReleaseEnvironmentNodeProps = getNodeProps(env, this.props.definitionEnvironmentCurrentReleaseMap, this.props.onReleaseFound, this.props.onNodeWithApprovalClicked, this.props.onNodeWithMIClicked);
            nodeProps.nodeWidth = this.props.envNodeWidth ? this.props.envNodeWidth : (this.props.envNodeMinWidth || 42);
            return (
                <EnvironmentNode
                className={this.props.releaseEnvironmentNodeCss}
                    // @ts-ignore TypeScript 3.9 upgrade
                    nodeRightMargin={this.props.envNodeRightMargin || 10}
                    key={nodeProps.environment.id}
                    projectId={this.props.projectId}
                    {...nodeProps}
                    preventClickOnNode={this.props.preventClickOnNode}
                />);
        });

        return nodes;
    }

    private _getShowMoreEnvironmentsButton(): JSX.Element {
        const hiddenEnvCount = this.props.visibleEnvCount ? this.props.environments.length - this.props.visibleEnvCount : 0;
        return (<div onClick={this._expandEnvironments} data-is-focusable={true} tabIndex={0} onKeyDown={this._onShowMoreButtonKeyPress} className={"active-release-show-more-button"}>
            <Icon iconName="Add" className="active-release-show-more-icon" />
            <span>{localeFormat("{0}", hiddenEnvCount)}</span>
        </div>);
    }

    private _expandEnvironments = (): void => {
        this.props.onExpandClick(this._getChainWidthOnExpand());
    }

    private _onShowMoreButtonKeyPress = (event: React.KeyboardEvent<HTMLElement>): void => {
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            this._expandEnvironments();
        }
    }

    private _getChainWidthOnExpand(): number {
        const envCount: number = this.props.environments.length;
        const nodeWidth: number = this.props.envNodeWidth ? this.props.envNodeWidth : (this.props.envNodeMinWidth || 42);
        return envCount * (nodeWidth + (this.props.envNodeRightMargin || 10)) + 20; // +10 to account for margin; +20 for show more button which was subtracted in collapsed state
    }

}

function getNodeProps(releaseEnvironment: ReleaseEnvironment, definitionEnvironmentCurrentReleaseMap?: { [key: number]: number }, onReleaseFoundCallback?: () => void, onNodeWithApprovalClicked?: (envId: number) => void, onNodeWithMIClicked?: (envId: number) => void): IActiveReleaseEnvironmentNodeProps {
    let pendingApprovals: ReleaseApproval[] = [];

    const latestDeploymentAttempt = getLatestDeploymentAttempt(releaseEnvironment.deploySteps);
    if (!latestDeploymentAttempt
        || latestDeploymentAttempt.operationStatus === DeploymentOperationStatus.Pending) {

        const isValidPendingApproval = (app: ReleaseApproval) => { return app.status === ApprovalStatus.Pending && app.id && app.approver; }
        pendingApprovals.push(...(releaseEnvironment.preDeployApprovals || []).filter(isValidPendingApproval));
        pendingApprovals.push(...(releaseEnvironment.postDeployApprovals || []).filter(isValidPendingApproval));
    }

    return {
        environment: releaseEnvironment,
        pendingApprovals: pendingApprovals,
        isLastDeployed: definitionEnvironmentCurrentReleaseMap && definitionEnvironmentCurrentReleaseMap[releaseEnvironment.definitionEnvironmentId] === releaseEnvironment.release.id,
        onReleaseFound: onReleaseFoundCallback,
        onNodeWithApprovalClicked: onNodeWithApprovalClicked ? () => onNodeWithApprovalClicked(releaseEnvironment.id) : undefined,
        onNodeWithMIClicked: onNodeWithMIClicked ? () => onNodeWithMIClicked(releaseEnvironment.id) : undefined
    } as IActiveReleaseEnvironmentNodeProps;
}
